import { ReactNode } from 'react';
import { differenceInDays, format, isSameMonth } from 'date-fns';

import { CustomerServiceType } from '@/app/(app)/customers/[id]/provider';
import { ApiSchemas } from '@/components/providers/v1-api';

import { SmartSuggestionIcon } from '../ui/icons';
import {
  calculateNextServiceDate,
  formatDuePeriod,
  getLastCompletedEvent,
  getScheduledEvents,
  isReservice,
} from './booking-utils';

interface Props {
  customer: ApiSchemas['CustomerReadComplete'];
  serviceType: CustomerServiceType;
  servicePlan: ApiSchemas['ServicePlanRead'];
  selectedDate: Date;
}

export function ServiceTypeSuggestion({ customer, serviceType, servicePlan, selectedDate }: Props) {
  const rescheduleAllowed = serviceType.template?.reschedule_allowed ?? false;
  const rescheduleWindow = serviceType.template?.reschedule_move_days ?? 0;
  const rescheduleRequiresSameMonth = serviceType.template?.reschedule_require_same_month ?? false;

  const today = new Date();

  const lastCompletedService = getLastCompletedEvent(customer, serviceType);

  // check if previous service was within re-service window
  // TODO: should this be a setting within the service type template?
  const defaultReserviceWindow = 15;
  if (isReservice(defaultReserviceWindow, lastCompletedService))
    return <ServiceTypeTip>{servicePlan.name} service happened recently. Book a reservice.</ServiceTypeTip>;

  const closestScheduledEvent = getScheduledEvents(customer, serviceType)[0];
  const hasScheduledEvent = !!closestScheduledEvent;

  const nextServiceDate = calculateNextServiceDate(customer, serviceType);
  if (!nextServiceDate) return null;

  // Step 1: Is subscription service due? (current or overdue)
  const isServiceCurrent = isSameMonth(nextServiceDate, today) || nextServiceDate <= today;

  // Step 2: If no service is due, this is a service call
  if (!isServiceCurrent) {
    return <ServiceTypeTip>No {servicePlan.name} service is due. Schedule a service call.</ServiceTypeTip>;
  }

  // Step 3: Service is due - determine if this can be rescheduled or needs new booking
  const withinWindow = Math.abs(differenceInDays(nextServiceDate, selectedDate)) <= rescheduleWindow;
  const sameMonth = isSameMonth(nextServiceDate, selectedDate);
  const canReschedule = rescheduleAllowed && (rescheduleRequiresSameMonth ? withinWindow && sameMonth : withinWindow);

  // If can reschedule and has scheduled event, show reschedule option
  if (canReschedule && hasScheduledEvent) {
    const scheduledDate = new Date(closestScheduledEvent.event.date);
    return (
      <ServiceTypeTip>
        {servicePlan.name} service {scheduledDate < today ? ' was scheduled for ' : ' scheduled for '}{' '}
        <strong>{format(scheduledDate, 'EEE, MMM d')}</strong>. Try rescheduling this service.
      </ServiceTypeTip>
    );
  }

  return (
    <ServiceTypeTip>
      {servicePlan.name} service due <strong>{formatDuePeriod(nextServiceDate, serviceType)}</strong>. Try booking this
      service.
    </ServiceTypeTip>
  );
}

const ServiceTypeTip = ({ children }: { children: ReactNode }) => {
  return (
    <div className='flex items-center gap-4 rounded-lg p-2' style={{ backgroundColor: '#FEF3D5' }}>
      <div className='flex h-9 w-9 rounded-md' style={{ backgroundColor: '#FEE69C' }}>
        <SmartSuggestionIcon />
      </div>
      <div className='text-sm text-gray-700'>{children}</div>
    </div>
  );
};
